import { useState, useEffect, useCallback, useRef } from "react";
import announcementsApi from "../api/announcements";

const POLL_MS = 60_000;

function storageKey(prefix, uid) {
  return `ep_ann_${prefix}_${uid}`;
}

function loadSet(key) {
  try { return new Set(JSON.parse(sessionStorage.getItem(key) || "[]")); }
  catch { return new Set(); }
}

function saveSet(key, set) {
  try { sessionStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

export function useAnnouncements(user) {
  const uid = user?._id || user?.id || "anon";
  const ackKey  = storageKey("ack", uid);
  const readKey = storageKey("read", uid);

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [acknowledged, setAcknowledged]   = useState(() => loadSet(ackKey));
  const [read, setRead]                   = useState(() => loadSet(readKey));
  const timerRef = useRef(null);

  const fetchAnnouncements = useCallback(async () => {
    if (!uid || uid === "anon") return;
    try {
      setLoading(true);
      const data = await announcementsApi.list();
      const list = Array.isArray(data) ? data : (data?.announcements || data?.data || []);
      setAnnouncements(list);
    } catch {
      // silently retain existing list
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchAnnouncements();
    timerRef.current = setInterval(fetchAnnouncements, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchAnnouncements]);

  const acknowledge = useCallback(async (id) => {
    const nextAck  = new Set([...acknowledged, id]);
    const nextRead = new Set([...read, id]);
    setAcknowledged(nextAck);
    setRead(nextRead);
    saveSet(ackKey,  nextAck);
    saveSet(readKey, nextRead);
    try { await announcementsApi.acknowledge(id); } catch {}
  }, [acknowledged, read, ackKey, readKey]);

  const markRead = useCallback(async (id) => {
    const next = new Set([...read, id]);
    setRead(next);
    saveSet(readKey, next);
    try { await announcementsApi.markRead(id); } catch {}
  }, [read, readKey]);

  const create = useCallback(async (payload) => {
    try {
      const created = await announcementsApi.create(payload);
      const item = created?.announcement || created;
      setAnnouncements(prev => [item, ...prev]);
      return item;
    } catch (err) {
      // Optimistic local add so the page still shows it
      const local = {
        _id: `local_${Date.now()}`,
        ...payload,
        createdBy: { _id: uid, name: user?.name || "You", role: user?.role || "staff" },
        createdAt: new Date().toISOString(),
        acknowledgedBy: [],
        readBy: [],
      };
      setAnnouncements(prev => [local, ...prev]);
      return local;
    }
  }, [uid, user?.name, user?.role]);

  const remove = useCallback(async (id) => {
    setAnnouncements(prev => prev.filter(a => a._id !== id));
    try { await announcementsApi.remove(id); } catch {}
  }, []);

  // Filter announcements visible to the current user's role
  const visibleAnnouncements = announcements.filter(a => {
    const role    = user?.role;
    const myClass = user?.student?.class || user?.class;
    const myId    = user?._id || user?.id;

    if (!role || role === "admin" || role === "saas_admin") return true;

    const { targetType, targetIds = [] } = a;
    if (targetType === "all") return true;
    if (role === "student") {
      if (targetType === "all_students") return true;
      if (targetType === "class" && myClass && targetIds.includes(myClass)) return true;
      return false;
    }
    if (role === "teacher" || role === "coordinator") {
      if (targetType === "all_teachers") return true;
      if (targetType === "teacher" && targetIds.includes(myId)) return true;
      return false;
    }
    return true;
  });

  const unreadCount = visibleAnnouncements.filter(a =>
    !acknowledged.has(a._id) && !read.has(a._id)
  ).length;

  const pendingAck = visibleAnnouncements.filter(a =>
    a.requiresAck && !acknowledged.has(a._id)
  );

  return {
    announcements: visibleAnnouncements,
    allAnnouncements: announcements,
    loading,
    unreadCount,
    pendingAck,
    acknowledged,
    read,
    acknowledge,
    markRead,
    create,
    remove,
    refresh: fetchAnnouncements,
  };
}
